import Flex from 'components/ui/Flex';
import Spinner from 'components/ui/Spinner';
import Text from 'components/ui/Text';
import useAppDispatch from 'hooks/useAppDispatch';
import useAppSelector from 'hooks/useAppSelector';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import ErrorDisplay from 'components/Error/ErrorDisplay';
import ItemCountDisplay from 'components/ui/ItemCountDisplay';
import Pagination from 'components/ui/Pagination';
import Table, { TableCell, SortableHeaderElement } from 'components/ui/Table';
import useBreakPoints from 'hooks/useBreakPoints';
import { useTranslation } from 'react-i18next';
import {
    cardLoadingSet as cardloadingSet,
    clearReturnSlip as clearReturnSlipSet,
    clearErrors,
    selectedReturnSet,
    returnSelectors,
    returnThunks,
    setFilterParams,
    selectSearchFilters,
    FilterParams,
    SEARCH_MODE,
    OrderByField,
} from 'store/reducers/returnSlice';
import { styled } from 'styled-components';
import { getStatusInfo, getStatusTypeString } from 'utils/helpers';
import RowActionsMenu from 'components/ui/RowActionsMenu';
import { faTrash } from '@fortawesome/pro-solid-svg-icons';
import { ReturnOverviewDto, ReturnStatusType } from 'returnCenter-api';
import NoResultsFound from 'components/ui/NoResultsFound';
import { faExclamationTriangle } from '@fortawesome/pro-light-svg-icons';
import { ContentContainer } from 'components/ui/ContentContainer';
import CssSphere from 'components/ui/CssSphere';
import { getStatusColor } from 'components/Orders/OrderStatus';
import ReturnSummaryCell from 'components/ui/ReturnSummaryCell';
import ConfirmationDialog from 'components/ui/ConfirmationDialog';
import ReturnFilters, { buildFilters, isB2CAttention } from 'components/Filters/ReturnFilters';
import B2CStatusBadge from 'components/B2C/B2CStatusBadge';
import { getDateMonthsAgo } from 'utils/dates';
import { setSelectedStatus } from 'store/reducers/navigationSlice';

const SEARCH_PAGE_SIZE = 10;

const ReturnStatus = () => {
    const dispatch = useAppDispatch();
    const { status } = useParams();
    const bp = useBreakPoints();
    const { t } = useTranslation();

    const [refetchTrigger, setRefetchTrigger] = useState(0);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [deleteReturnId, setDeleteReturnId] = useState<number | undefined>(undefined);
    const [deleteCreditNotes, setDeleteCreditNotes] = useState<string[]>([]);

    const returns = useAppSelector(returnSelectors.selectSearchReturns);
    const searchReturnPageInfo = useAppSelector(returnSelectors.selectSearchReturnPageInfo);
    const loading = useAppSelector(returnSelectors.selectSearchReturnsLoading);
    const error = useAppSelector(returnSelectors.selectSearchReturnErrorOnLoading);
    const errorMessage = useAppSelector(returnSelectors.selectErrorMessage);
    const searchFilters = useAppSelector(selectSearchFilters);

    const navigate = useNavigate();

    const { type: statusType, heading } = getStatusInfo(status, t);

    const compact = bp.width < 900;
    const addPadding = bp.width < 1520;

    const resetFilters: FilterParams = {
        query: '',
        filterInput: undefined,
        searchMode: SEARCH_MODE.NONE,
        from: getDateMonthsAgo(6),
        to: new Date(),
        orderBy: 'Id',
        orderByDirection: 'DESC',
        b2cStatus: undefined,
        b2cAttention: false,
    };

    useEffect(() => {
        if (statusType !== undefined) {
            const filters = buildFilters(resetFilters, statusType);
            dispatch(cardloadingSet({ type: getStatusTypeString(statusType), isLoading: true }));
            dispatch(setSelectedStatus(statusType));
            dispatch(setFilterParams(resetFilters));
            dispatch(
                returnThunks.searchReturns({
                    returnOverviewRequest: {
                        page: searchReturnPageInfo?.currentPage ?? 1,
                        size: SEARCH_PAGE_SIZE,
                        filters: filters,
                        orderBy: resetFilters.orderBy,
                        orderByDirection: resetFilters.orderByDirection,
                    },
                }),
            );
        }
        // Not on a page change: paging searches with the current filters itself.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, statusType, refetchTrigger]);

    const handleDeleteReturn = useCallback(async (item: ReturnOverviewDto) => {
        if (!item.id) return;

        const creditNoteNumbers: string[] = (item.creditNotes ?? [])
            .map((cn) => cn.orderNumber)
            .filter((note): note is string => Boolean(note));
        setDeleteCreditNotes(creditNoteNumbers);
        setDeleteReturnId(item.id);
        setShowDeleteDialog(true);
    }, []);

    const handleConfirmDeleteReturn = useCallback(async () => {
        if (!deleteReturnId) return;
        await dispatch(returnThunks.deleteReturnSlip(deleteReturnId));
        setRefetchTrigger((prev) => prev + 1);
        setShowDeleteDialog(false);
        setDeleteReturnId(undefined);
        setDeleteCreditNotes([]);
    }, [dispatch, deleteReturnId]);

    const handleCancelDeleteReturn = useCallback(() => {
        setShowDeleteDialog(false);
        setDeleteReturnId(undefined);
        setDeleteCreditNotes([]);
    }, []);

    const handleSort = useCallback(
        (field: OrderByField) => {
            const isSameField = searchFilters.orderBy === field;
            const newDirection = isSameField && searchFilters.orderByDirection === 'DESC' ? 'ASC' : 'DESC';
            const updated: FilterParams = { ...searchFilters, orderBy: field, orderByDirection: newDirection };
            dispatch(setFilterParams(updated));
            dispatch(
                returnThunks.searchReturns({
                    returnOverviewRequest: {
                        page: searchReturnPageInfo?.currentPage ?? 1,
                        size: SEARCH_PAGE_SIZE,
                        filters: statusType !== undefined ? buildFilters(updated, statusType) : undefined,
                        startDate: updated.from?.toISOString(),
                        endDate: updated.to?.toISOString(),
                        search: updated.query ?? '',
                        orderBy: updated.orderBy,
                        orderByDirection: updated.orderByDirection,
                        b2CAttention: isB2CAttention(updated, statusType),
                    },
                }),
            );
        },
        [dispatch, searchFilters, statusType, searchReturnPageInfo],
    );

    const tableCols = [
        <SortableHeaderElement
            label={t('returnId')}
            active={searchFilters.orderBy === 'Id'}
            desc={searchFilters.orderByDirection === 'DESC'}
            onClick={() => handleSort('Id')}
        />,
        t('orderNumber', 'Order number(s)'),
        ...(statusType === ReturnStatusType.Completed ? [t('creditNotes', 'Credit note(s)')] : []),
        t('returnSummary', 'Return Summary'),
        t('returnedItems'),
        <SortableHeaderElement
            label={t('created')}
            active={searchFilters.orderBy === 'Created'}
            desc={searchFilters.orderByDirection === 'DESC'}
            onClick={() => handleSort('Created')}
        />,
        <SortableHeaderElement
            label={t('changed')}
            active={searchFilters.orderBy === 'Changed'}
            desc={searchFilters.orderByDirection === 'DESC'}
            onClick={() => handleSort('Changed')}
        />,
        ...(statusType === ReturnStatusType.Completed ? [t('completedByUser', 'Completed by')] : []),
        t('comment'),
        '',
    ];

    const rows = useMemo(() => {
        return (returns || []).map((item) => {
            const cells: TableCell[] = [
                {
                    el: (
                        <Flex gap="0.25rem" align="center" justify="center">
                            <CssSphere size={8} color={getStatusColor(statusType ?? null)} /> {item.id}
                            <B2CStatusBadge status={item.b2CStatus} />
                        </Flex>
                    ),
                },
                {
                    el: (
                        <Flex column gap="0.25rem">
                            {item.orderNumbers?.map((orderNumber, index) => (
                                <Text margin="0" padding="0" key={index}>
                                    {orderNumber}
                                </Text>
                            ))}
                        </Flex>
                    ),
                },
                ...(statusType === ReturnStatusType.Completed
                    ? [
                          {
                              el: (
                                  <Flex column gap="0.25rem">
                                      {item.creditNotes?.map((creditNote, index) => (
                                          <Text margin="0" padding="0" key={index}>
                                              {creditNote.orderNumber}
                                          </Text>
                                      ))}
                                  </Flex>
                              ),
                          },
                      ]
                    : []),
                { el: <ReturnSummaryCell returnSummaries={item.returnSummaries} /> },
                { el: <Text>{item.totalReturnItems}</Text> },
                { el: <Text>{item.created?.str}</Text> },
                { el: <Text>{item.changed?.str}</Text> },
                ...(statusType === ReturnStatusType.Completed ? [{ el: <Text>{item.completedByUser}</Text> }] : []),
                {
                    el: (
                        <Flex column gap="0.25rem">
                            <ul>
                                {item.pinnedLogs?.map((pinnedLogs, index) => (
                                    <li key={index}>{pinnedLogs.comment}</li>
                                ))}
                            </ul>
                        </Flex>
                    ),
                },
                {
                    el: (
                        <Flex className="pointer-cursor">
                            <RowActionsMenu
                                className="pointer-cursor"
                                disabled={item.isLockedByBatch}
                                actions={[
                                    {
                                        icon: faTrash,
                                        label: t('delete'),
                                        onClick: () => handleDeleteReturn(item),
                                    },
                                ]}
                            />
                        </Flex>
                    ),
                    overflow: 'visible',
                },
            ];

            const handleRowClick = () => {
                const returnId = item.id;
                if (!returnId) {
                    return;
                }
                dispatch(clearErrors());
                dispatch(clearReturnSlipSet());
                dispatch(selectedReturnSet(returnId));
                navigate(`/returns/${getStatusTypeString(statusType)}/${returnId}`, {
                    state: {
                        origin: getStatusTypeString(statusType),
                        returnIdFromPath: returnId,
                    },
                });
            };

            return {
                onClick: handleRowClick,
                cells,
            };
        });
    }, [returns, handleDeleteReturn, statusType, dispatch, navigate]);

    if (error) {
        return (
            <ErrorDisplay
                icon={faExclamationTriangle}
                errorMessage={errorMessage}
                onBack={() => {
                    dispatch(clearErrors());
                    navigate(-1);
                }}
                title={t('error_occurred')!}
            />
        );
    }

    return (
        <Wrapper column $padding={addPadding}>
            <ContentContainer column gap="1rem" className="returns-table-wrapper mt-3">
                <Heading lineheight={20} fontSize={32} as="h2">
                    {heading}
                </Heading>

                <StyledContainer column>
                    <ReturnFilters statusType={statusType} pageSize={SEARCH_PAGE_SIZE} reset={resetFilters} />
                </StyledContainer>
                <ItemCountDisplay
                    marginTop={false}
                    title={t('returns')}
                    count={returns.length}
                    countLabel={t('returns')}
                />

                {returns.length === 0 ? (
                    <NoResultsFound />
                ) : loading ? (
                    <SpinnerWrapper column $grow={1} gap="1rem" align="center">
                        <Spinner useFullHeight />
                    </SpinnerWrapper>
                ) : (
                    <>
                        <Table
                            bgColor="fillWhite"
                            headers={tableCols}
                            cols={tableCols.length}
                            rows={rows}
                            className="table"
                            compact={compact}
                            renderCompactHeaders={compact}
                            arrow
                            arrowLabel={t('show')!}
                        />
                        <Pagination
                            page={searchReturnPageInfo?.currentPage!}
                            pages={searchReturnPageInfo?.totalPages!}
                            pageSize={searchReturnPageInfo?.pageSize!}
                            itemCount={returns.length}
                            itemsTotal={searchReturnPageInfo?.totalItems!}
                            label={''}
                            setPage={(page: number) =>
                                dispatch(
                                    returnThunks.searchReturns({
                                        returnOverviewRequest: {
                                            page,
                                            size: SEARCH_PAGE_SIZE,
                                            filters:
                                                statusType !== undefined
                                                    ? buildFilters(searchFilters, statusType)
                                                    : undefined,
                                            startDate: searchFilters.from?.toISOString(),
                                            endDate: searchFilters.to?.toISOString(),
                                            search: searchFilters.query ?? '',
                                            orderBy: searchFilters.orderBy,
                                            orderByDirection: searchFilters.orderByDirection,
                                            b2CAttention: isB2CAttention(searchFilters, statusType),
                                        },
                                    }),
                                )
                            }
                            compact={false}
                        />

                        <ConfirmationDialog
                            open={showDeleteDialog}
                            onCancel={handleCancelDeleteReturn}
                            onClose={handleCancelDeleteReturn}
                            onConfirm={handleConfirmDeleteReturn}
                            title={t('confirmDeletion', 'Confirm Deletion')}
                            content={
                                statusType === ReturnStatusType.Completed && deleteCreditNotes.length
                                    ? t('confirmDeleteReturnWithCredit', {
                                          returnId: deleteReturnId,
                                          notes: deleteCreditNotes.join(', '),
                                      })
                                    : t('confirmDeleteReturnMessage', { returnId: deleteReturnId })
                            }
                        />
                    </>
                )}
            </ContentContainer>
        </Wrapper>
    );
};

export default ReturnStatus;

const Wrapper = styled(Flex)<{ $padding?: boolean }>`
    flex: 1;
    margin: 0 auto;
    padding: ${({ $padding }) => ($padding ? '1rem' : '1rem 0 0 0')};
`;

const Heading = styled(Text)`
    padding: 0 !important;
`;

const SpinnerWrapper = styled(Flex)`
    margin-top: 3rem;
`;

const StyledContainer = styled(Flex)`
    padding: 0.5rem;
    gap: 0.5rem;
    background-color: ${({ theme }) => theme.colors.fillWhite};
    border-radius: var(--radius);
`;
